import { useState } from 'react';

import { api } from '../api.ts';
import { useModosithat } from '../felhasznalo-context.ts';

/**
 * A kimenő levelek kísérő szövegei. (3.0 / 4., 2026-09-28)
 *
 * A dokumentum sablonja azt mondja meg, **mi áll az okmányon**; ez azt,
 * **mi áll a levélben**, amivel kimegy. Levéltípusonként egy tárgy és egy
 * törzs — a hangnem és az aláírás a cégé.
 *
 * A kapcsos zárójeles részek a küldéskor kicserélődnek (`{partner}` a
 * címzett neve és így tovább). Amit a program nem ismer fel, az
 * változatlanul marad a levélben: jobb egy ottfelejtett jelölés, mint egy
 * hiányos mondat.
 */

export interface LevelSablon {
  tipus: string;
  targy: string;
  torzs: string;
  valtozok: Array<{ kulcs: string; mit: string }>;
}

const TIPUS_NEVEK: Record<string, string> = {
  szallitolevel: 'Szállítólevél',
  kiadas: 'Átadási dokumentumok',
  szamla: 'Számla',
  felszolitas: 'Fizetési felszólítás',
  szerzodes: 'Szerződés',
};

export function KiseroLevelek({
  levelek,
  onValtozott,
  onHiba,
}: {
  levelek: LevelSablon[];
  onValtozott: (uj: LevelSablon) => void;
  onHiba: (uzenet: string) => void;
}) {
  const modosithat = useModosithat('email');
  const [aktiv, setAktiv] = useState(levelek[0]?.tipus ?? 'szallitolevel');
  const [piszkozat, setPiszkozat] = useState<Record<string, string>>({});
  const [uzenet, setUzenet] = useState<string | null>(null);

  const talalt = levelek.find((l) => l.tipus === aktiv) ?? levelek[0];
  if (!talalt) return null;
  const level = talalt;

  const kulcs = (mezo: string) => `${level.tipus}.${mezo}`;
  const ertek = (mezo: 'targy' | 'torzs') => piszkozat[kulcs(mezo)] ?? level[mezo];
  const valtozott = (mezo: 'targy' | 'torzs') => ertek(mezo) !== level[mezo];

  async function ment(mezo: 'targy' | 'torzs') {
    try {
      const v = await api.patch<{ level: LevelSablon }>(
        `/api/email/level/${level.tipus}/${mezo}`,
        { ertek: ertek(mezo) },
      );
      onValtozott(v.level);
      setPiszkozat((p) => {
        const uj = { ...p };
        delete uj[kulcs(mezo)];
        return uj;
      });
      setUzenet('Mentve. A mostantól kiküldött levelekre ez a szöveg kerül.');
      setTimeout(() => setUzenet(null), 4000);
    } catch (h) {
      onHiba(h instanceof Error ? h.message : 'A mentés nem sikerült.');
    }
  }

  return (
    <>
      <div className="vsw" style={{ marginTop: 12 }}>
        {levelek.map((l) => (
          <button
            key={l.tipus}
            className={l.tipus === aktiv ? 'on' : undefined}
            onClick={() => setAktiv(l.tipus)}
          >
            {TIPUS_NEVEK[l.tipus] ?? l.tipus}
          </button>
        ))}
      </div>

      {uzenet && <div className="uzenet ok">{uzenet}</div>}

      <div className="panel" style={{ marginTop: 12 }}>
        <p className="lbl">A levél tárgya</p>
        <input
          className="sabi"
          value={ertek('targy')}
          disabled={!modosithat}
          onChange={(e) => setPiszkozat((p) => ({ ...p, [kulcs('targy')]: e.target.value }))}
        />
        {valtozott('targy') && modosithat && (
          <div className="btns" style={{ marginTop: 6 }}>
            <button className="btn pri sm" onClick={() => void ment('targy')}>
              Mentés
            </button>
            <button
              className="btn sm"
              onClick={() =>
                setPiszkozat((p) => {
                  const uj = { ...p };
                  delete uj[kulcs('targy')];
                  return uj;
                })
              }
            >
              Mégse
            </button>
          </div>
        )}

        <p className="lbl" style={{ marginTop: 14 }}>
          A levél szövege
        </p>
        <textarea
          className="sabt"
          rows={14}
          value={ertek('torzs')}
          disabled={!modosithat}
          onChange={(e) => setPiszkozat((p) => ({ ...p, [kulcs('torzs')]: e.target.value }))}
        />
        {valtozott('torzs') && modosithat && (
          <div className="btns" style={{ marginTop: 6 }}>
            <button className="btn pri sm" onClick={() => void ment('torzs')}>
              Mentés
            </button>
            <button
              className="btn sm"
              onClick={() =>
                setPiszkozat((p) => {
                  const uj = { ...p };
                  delete uj[kulcs('torzs')];
                  return uj;
                })
              }
            >
              Mégse
            </button>
          </div>
        )}

        <p className="lbl" style={{ marginTop: 14 }}>
          Mit lehet beleírni
        </p>
        <div className="valtozok">
          {level.valtozok.map((v) => (
            <span key={v.kulcs} title={v.mit}>
              <code>{`{${v.kulcs}}`}</code> {v.mit}
            </span>
          ))}
        </div>
        <div className="hint" style={{ marginTop: 8 }}>
          A kapcsos zárójeles részek küldéskor kicserélődnek. A dokumentum magát a levelet
          csatolmányként kíséri — azt a <b>Dokumentum sablonok</b> fülön lehet állítani.
        </div>
      </div>
    </>
  );
}
